import { useEffect, useState } from "react";
import { collection, query, orderBy, limit, getDocs, getFirestore } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const parseDate = (d: any): Date => {
    if (!d) return new Date(0);
    if (d instanceof Date) return d;
    if (typeof d.toDate === "function") return d.toDate();
    if (d.seconds) return new Date(d.seconds * 1000);
    return new Date(d);
};

export function HomeView() {
    const [latestUsers, setLatestUsers] = useState<any[]>([]);
    const [stats, setStats] = useState({
        totalUsers: 0,
        activeToday: 0,
        purchasesToday: 0
    });
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const firestore = getFirestore();

    useEffect(() => {
        async function loadHomeData() {
            setLoading(true);
            try {
                // Fetch latest 5 users
                const usersQuery = query(
                    collection(firestore, "users"),
                    orderBy("first_seen", "desc"),
                    limit(6)
                );
                const usersSnap = await getDocs(usersQuery);
                setLatestUsers(usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

                // Fetch total counts
                const allUsersSnap = await getDocs(collection(firestore, "users"));
                const allPurchasesSnap = await getDocs(collection(firestore, "purchases"));

                const now = new Date();
                const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

                const todayUsers = allUsersSnap.docs.filter(d => parseDate(d.data().first_seen) >= todayStart).length;
                const todayPurchases = allPurchasesSnap.docs.filter(d => parseDate(d.data().created_at) >= todayStart).length;

                setStats({
                    totalUsers: allUsersSnap.size,
                    activeToday: todayUsers,
                    purchasesToday: todayPurchases
                });

            } catch (error) {
                console.error("Error loading home data:", error);
            } finally {
                setLoading(false);
            }
        }
        loadHomeData();
    }, []);

    const quickActions = [
        { title: "Manage Users", icon: "👥", path: "/c/users", color: "bg-blue-500" },
        { title: "Configuration", icon: "⚙️", path: "/configuration", color: "bg-purple-500" },
        { title: "View Analytics", icon: "📊", path: "/analytics", color: "bg-emerald-500" },
        { title: "Campaigns", icon: "📢", path: "/c/campaigns", color: "bg-orange-500" },
        { title: "Create Campaign", icon: "🚀", path: "/create-campaign", color: "bg-indigo-500" },
        { title: "Promo Codes", icon: "🎟️", path: "/c/promo_codes", color: "bg-amber-500" },
        { title: "NFT Management", icon: "🏅", path: "/nft-management", color: "bg-teal-500" },
    ];

    return (
        <div className="p-8 max-w-6xl mx-auto space-y-10 min-h-screen">
            {/* Header section */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div>
                    <h1 className="text-4xl font-black text-white tracking-tight uppercase">Dashboard</h1>
                    <p className="text-slate-500 mt-2 text-lg">Platform overview and quick shortcuts.</p>
                </div>
                <div className="flex bg-slate-900 border border-slate-800 rounded-2xl p-1 px-4 py-2 self-start md:self-auto">
                    <span className="text-emerald-500 mr-2">●</span>
                    <span className="text-slate-300 text-sm font-medium">System Online</span>
                </div>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-sm hover:shadow-blue-900/10 transition-shadow border-b-4 border-b-blue-500">
                    <p className="text-slate-500 text-sm font-bold uppercase tracking-wider mb-1">Total Users</p>
                    <div className="flex items-baseline gap-2">
                        <h2 className="text-4xl font-black text-white">{stats.totalUsers.toLocaleString()}</h2>
                        <span className="text-emerald-500 text-sm font-bold">Live</span>
                    </div>
                </div>
                <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-sm hover:shadow-emerald-900/10 transition-shadow border-b-4 border-b-emerald-500">
                    <p className="text-slate-500 text-sm font-bold uppercase tracking-wider mb-1">New Today</p>
                    <div className="flex items-baseline gap-2">
                        <h2 className="text-4xl font-black text-white">+{stats.activeToday}</h2>
                        <p className="text-slate-500 text-xs">Since midnight</p>
                    </div>
                </div>
                <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-sm hover:shadow-orange-900/10 transition-shadow border-b-4 border-b-orange-500">
                    <p className="text-slate-500 text-sm font-bold uppercase tracking-wider mb-1">Purchases Today</p>
                    <div className="flex items-baseline gap-2">
                        <h2 className="text-4xl font-black text-white">{stats.purchasesToday}</h2>
                        <p className="text-slate-500 text-xs text-orange-400">Transactions</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                {/* Quick Actions */}
                <div className="lg:col-span-1 space-y-6">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <span className="w-1.5 h-6 bg-blue-500 rounded-full"></span>
                        Quick Actions
                    </h3>
                    <div className="grid grid-cols-1 gap-4">
                        {quickActions.map((action) => (
                            <button
                                key={action.path}
                                onClick={() => navigate(action.path)}
                                className="flex items-center gap-4 p-4 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-2xl transition-all group text-left"
                            >
                                <div className={`w-12 h-12 ${action.color} rounded-xl flex items-center justify-center text-2xl group-hover:scale-110 transition-transform shadow-lg shadow-black/20`}>
                                    {action.icon}
                                </div>
                                <div>
                                    <p className="text-white font-bold">{action.title}</p>
                                    <p className="text-slate-500 text-xs">Navigate to {action.title.toLowerCase()}</p>
                                </div>
                                <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                                    <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Latest Signups */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="flex items-center justify-between">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                            <span className="w-1.5 h-6 bg-purple-500 rounded-full"></span>
                            Recent Signups
                        </h3>
                        <button
                            onClick={() => navigate("/c/users")}
                            className="text-blue-500 hover:text-blue-400 text-sm font-bold transition-colors"
                        >
                            View All →
                        </button>
                    </div>

                    <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-950/50">
                                    <th className="p-4 text-slate-500 text-xs font-bold uppercase tracking-widest border-b border-slate-800">User</th>
                                    <th className="p-4 text-slate-500 text-xs font-bold uppercase tracking-widest border-b border-slate-800">Tier</th>
                                    <th className="p-4 text-slate-500 text-xs font-bold uppercase tracking-widest border-b border-slate-800">Joined</th>
                                    <th className="p-4 text-slate-500 text-xs font-bold uppercase tracking-widest border-b border-slate-800"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    Array(5).fill(0).map((_, i) => (
                                        <tr key={i} className="animate-pulse">
                                            <td className="p-4 border-b border-slate-800/50"><div className="h-4 bg-slate-800 rounded w-24"></div></td>
                                            <td className="p-4 border-b border-slate-800/50"><div className="h-4 bg-slate-800 rounded w-16"></div></td>
                                            <td className="p-4 border-b border-slate-800/50"><div className="h-4 bg-slate-800 rounded w-20"></div></td>
                                            <td className="p-4 border-b border-slate-800/50"></td>
                                        </tr>
                                    ))
                                ) : latestUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="p-10 text-center text-slate-500">No users found.</td>
                                    </tr>
                                ) : (
                                    latestUsers.map((user) => (
                                        <tr key={user.id} className="hover:bg-slate-800/30 transition-colors">
                                            <td className="p-4 border-b border-slate-800/50">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-xs font-bold text-slate-400">
                                                        {user.full_name?.charAt(0) || user.username?.charAt(0) || "?"}
                                                    </div>
                                                    <div>
                                                        <p className="text-white font-medium text-sm">{user.full_name || user.username || "Anonymous"}</p>
                                                        <p className="text-slate-500 text-[10px]">{user.user_id}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-4 border-b border-slate-800/50">
                                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${user.subscription_tier === 'premium' ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20' :
                                                    user.subscription_tier === 'pro' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' :
                                                        'bg-slate-500/10 text-slate-400 border border-slate-500/20'
                                                    }`}>
                                                    {user.subscription_tier || 'free'}
                                                </span>
                                            </td>
                                            <td className="p-4 border-b border-slate-800/50 text-slate-400 text-xs">
                                                {user.first_seen ? parseDate(user.first_seen).toLocaleDateString() : 'N/A'}
                                            </td>
                                            <td className="p-4 border-b border-slate-800/50 text-right">
                                                <button
                                                    onClick={() => navigate(`/c/users/${user.id}`)}
                                                    className="p-1 px-3 text-xs bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors border border-slate-700"
                                                >
                                                    Edit
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
